import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { applyProfile, applyFavicon, logoSrc, cachedJSON } from "@/context/BrandingContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

function MfaStep({ mfa, code, setCode, trust, setTrust, resend, back }) {
  return (
    <div className="space-y-4" data-testid="login-mfa-step">
      <div>
        <Label htmlFor="mfa-code">{mfa.method === "email" ? "Bestätigungscode aus der Mail" : "Code aus der Authenticator-App"}</Label>
        <Input id="mfa-code" autoComplete="one-time-code" autoFocus value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
          required className="mt-1 tracking-[0.3em] text-center text-lg" placeholder="000000" data-testid="login-mfa-code" />
        <p className="text-xs text-slate-500 mt-1">Kein Zugriff? Gib stattdessen einen Deiner Recovery-Codes ein (z. B. A1B2C3-D4E5F6).</p>
      </div>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={trust} onChange={(e) => setTrust(e.target.checked)} data-testid="login-mfa-trust" /> Diesem Browser 30 Tage vertrauen</label>
      <div className="flex gap-4 text-xs">
        {mfa.method === "email" && <button type="button" onClick={resend} className="text-blue-600 hover:underline" data-testid="login-mfa-resend">Code erneut senden</button>}
        <button type="button" onClick={back} className="text-slate-500 hover:underline" data-testid="login-mfa-back">Zurück</button>
      </div>
    </div>
  );
}

export default function Login() {
  const { login, loginMfa, requestCode, loginWithCode } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("code");
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState("");
  const [info, setInfo] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [mfa, setMfa] = useState(null);
  const [trust, setTrust] = useState(true);
  const [brand, setBrand] = useState(() => cachedJSON("brand_public"));
  useEffect(() => {
    api.get("/branding/public").then((r) => {
      setBrand(r.data); applyProfile(r.data.profile); applyFavicon(r.data.favicon_url, r.data.favicon_svg_url);
      try { localStorage.setItem("brand_public", JSON.stringify(r.data)); } catch { /* ignore */ }
    }).catch(() => setBrand((b) => b || {}));
  }, []);
  const appName = brand?.app_name ?? "";
  const lightLogo = logoSrc(brand?.logos?.sidebar);
  const darkLogo = logoSrc(brand?.logos?.pdf);

  const run = async (fn) => {
    setError(""); setLoading(true);
    try { await fn(); } catch (err) { setError(apiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  const submit = (e) => {
    e.preventDefault();
    if (mfa) { run(async () => { await loginMfa(mfa.mfa_token, code, trust); navigate("/"); }); return; }
    if (mode === "password") {
      run(async () => {
        const data = await login(email, password);
        if (data.mfa_required) { setMfa(data); setCode(""); setInfo(data.method === "email" ? "Wir haben Dir einen Bestätigungscode per Mail gesendet." : "Gib den Code aus Deiner Authenticator-App ein."); return; }
        navigate("/");
      });
      return;
    }
    if (!codeSent) {
      run(async () => { const { data } = await requestCode(email); setInfo(data.message); setCodeSent(true); });
      return;
    }
    run(async () => { await loginWithCode(email, code); navigate("/"); });
  };

  const switchMode = (m) => { setMode(m); setError(""); setInfo(""); setCodeSent(false); setCode(""); setMfa(null); };
  const resend = () => run(async () => { await api.post("/auth/login/mfa/resend", { mfa_token: mfa.mfa_token }); setInfo("Neuer Code wurde gesendet."); });

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className={`relative hidden lg:block lg:order-2 transition-opacity duration-300 ${brand ? "opacity-100" : "opacity-0"}`}>
        <img
          src="https://images.unsplash.com/photo-1554068865-24cecd4e34b8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
          alt="Sport"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 brand-sidebar opacity-[0.88]" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12" style={{ color: "var(--sidebar-text)" }}>
          <div data-testid="login-brand">
            {lightLogo && <img src={lightLogo} alt={appName} className="max-h-24 max-w-xs object-contain mb-8" data-testid="login-logo" />}
            {appName && <div className="font-display text-3xl font-extrabold" data-testid="login-app-name">{appName}</div>}
          </div>
          <div>
            <h2 className="font-display text-4xl font-bold leading-tight" style={{ color: "var(--sidebar-text)" }}>{brand?.login_headline || brand?.app_subtitle || "Redaktionssystem"}</h2>
            <p className="mt-3 max-w-md whitespace-pre-line" style={{ color: "var(--sidebar-muted)" }} data-testid="login-text">
              {brand?.login_text || "Ausgaben planen, Artikel per Baukasten organisieren, Anzeigenkunden verwalten – alles an einem Ort."}
            </p>
          </div>
          <div className="text-xs space-y-1 text-center" style={{ color: "var(--sidebar-muted)" }} data-testid="login-footer">
            <div>{(brand?.footer_text || "").trim().startsWith("©") ? brand.footer_text : `© ${new Date().getFullYear()} ${brand?.footer_text || appName || "Club-Magazin"}`}</div>
            {brand?.version && <div data-testid="login-version">Version {brand.version.replace(/^v/i, "")}</div>}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 bg-white lg:order-1">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="lg:hidden mb-6" data-testid="login-mobile-brand">
            {darkLogo ? <img src={darkLogo} alt={appName} className="max-h-14 max-w-[240px] object-contain" />
              : <div className="font-display text-2xl font-extrabold text-[#0B132B]">{appName}</div>}
          </div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Anmelden</h1>
          <p className="text-slate-500 mt-1 mb-6 text-sm">
            {mode === "code" ? "Gib Deine E-Mail-Adresse ein – Du erhältst Deinen Anmelde-Code (10 Minuten gültig) per Mail zugesandt." : "Anmeldung mit Passwort (Chefredaktion)."}
          </p>
          <div className="flex gap-1 bg-slate-100 p-1 rounded-lg mb-6">
            {[["code", "Mit Code"], ["password", "Mit Passwort"]].map(([m, l]) => (
              <button key={m} type="button" onClick={() => switchMode(m)} data-testid={`login-mode-${m}`}
                className={`flex-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${mode === m ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>{l}</button>
            ))}
          </div>
          {info && !error && <div className="mb-4 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm px-3 py-2" data-testid="login-info">{info}</div>}

          {error && (
            <div className="mb-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2" data-testid="login-error">
              {error}
            </div>
          )}

          {mfa ? <MfaStep {...{ mfa, code, setCode, trust, setTrust, resend, back: () => switchMode("password") }} /> : <>
          <div className="space-y-4">
            <div>
              <Label htmlFor="email">E-Mail</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={mode === "code" && codeSent}
                required className="mt-1" data-testid="login-email" placeholder="name@verein.de" />
            </div>
            {mode === "password" && (
            <div>
              <Label htmlFor="password">Passwort</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                required className="mt-1" data-testid="login-password" placeholder="••••••••" />
            </div>
            )}
            {mode === "code" && codeSent && (
              <div>
                <Label htmlFor="code">Anmelde-Code</Label>
                <Input id="code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  required className="mt-1 tracking-[0.5em] text-center text-lg" data-testid="login-code" placeholder="000000" />
                <button type="button" onClick={() => switchMode("code")} className="mt-2 text-xs text-blue-600 hover:underline" data-testid="login-code-reset">Andere E-Mail / neuen Code anfordern</button>
              </div>
            )}
          </div>
          </>}

          <Button type="submit" disabled={loading}
            className="w-full mt-6 bg-[#0B132B] hover:bg-[#1C2541] text-white h-11 rounded-lg"
            data-testid="login-submit">
            {loading ? <Loader2 className="animate-spin" size={18} /> : mfa ? "Bestätigen" : mode === "code" && !codeSent ? "Code anfordern" : "Anmelden"}
          </Button>
        </form>
      </div>
    </div>
  );
}
